import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import clsx from 'clsx';
import { AlertTriangle, Ban, CheckCircle2, RotateCcw, Tag, Truck, X } from 'lucide-react';
import { Button } from '../../../../components/ui/Button';
import { PendingButton } from '../../../../components/ui/PendingButton';
import { ProgressBar } from '../../../../components/ui/ProgressBar';
import { ToneBadge } from '../../../../components/ui/ToneBadge';
import { ConfirmSheet } from '../../components/ConfirmSheet';
import { OperatorActionBar } from '../../components/OperatorActionBar';
import { OperatorScanPanel } from '../../components/OperatorScanPanel';
import { QueueList } from '../../components/QueueList';
import { ResultCard } from '../../components/ResultCard';
import { TaskMeta } from '../../components/TaskMeta';
import { taskEntered } from '../../../../features/operator/operatorDeviceSlice';
import { OPERATOR_FLOW, OPERATOR_FLOW_PATHS } from '../../../../features/operator/operatorConstants';
import {
  asSentence,
  orderCardTitle,
  returnState,
  selectCourierRelabelReady,
  selectCourierSendReady,
  selectCurrentOperatorWarehouseId,
  selectEnabledFlows,
} from '../../../../features/operator/operatorUtils';
import { selectIsRunning } from '../../../../features/feedback/feedbackSlice';
import {
  dispatchLoad,
  fetchDispatchQueue,
  loadDispatchOrder,
  repeatShipmentItem,
  retryFailedDispatch,
  scanDispatchCode,
  submitDispatch,
} from '../../../../features/operatorDispatch/operatorDispatchThunks';
import {
  dispatchLoadOrderRemoved,
  dispatchStationEntered,
  selectDispatchAccepting,
  selectDispatchSummary,
  selectShipmentRepeatLabel,
} from '../../../../features/operatorDispatch/operatorDispatchSlice';
import {
  MIXED_COURIERS_TEXT,
  RECHECK,
  canLoadByTap,
  courierLabelOf,
  couriersText,
  dispatchOrderName,
  failedResults,
  isOrderComplete,
  itemsText,
  itemsTotalOf,
  labelFixOf,
  leftOutText,
  loadOfferSheet,
  ordersText,
  progressText,
  resultTitle,
  scannedCountOf,
  scannedItemsText,
  submitLabel,
  takeOffLoadText,
} from '../../../../features/operatorDispatch/operatorDispatchUtils';

/** The queue's filter: all, or one courier. A chip per courier with a code; the count is the server's. */
const CourierChips = ({ couriers, active, onChange }) => {
  const chips = [{ code: '', label: 'Todos' }, ...couriers.filter((courier) => courier.code)];
  return (
    <div className="flex flex-wrap gap-2" role="group" aria-label="Filtrar por courier">
      {chips.map((chip) => (
        <Button
          key={chip.code || 'all'}
          variant={chip.code === active ? 'outline' : 'ghost'}
          size="lg"
          pill
          aria-pressed={chip.code === active}
          onClick={() => onChange(chip.code)}
          data-testid="dispatch-courier-chip"
          data-courier={chip.code}
        >
          {chip.count != null ? `${chip.label} · ${chip.count}` : chip.label}
        </Button>
      ))}
    </div>
  );
};

/**
 * An order of the queue. Its items go up the load by scanning them; tapping
 * it, while it can go up, offers to put them all up without scanning, behind
 * a sheet that says so. A blocked one says why it cannot go and, when its
 * label is the floor's to make (`fix`), opens its packing screen for it.
 */
const QueueOrder = ({ row, entry, disabled, onLoad, fix, onFix }) => {
  const blocked = row.dispatchable === false;
  const header = (
    <span className="flex items-start justify-between gap-3">
      <span className="block min-w-0">
        <span data-testid="dispatch-order-title" className="block break-words text-base font-bold text-ninja-dark dark:text-white">
          {orderCardTitle(row)}
        </span>
        <span className="block text-sm font-normal text-text-secondary">
          {[row.company_name, courierLabelOf(row), itemsText(Number(row.items_count) || 0)].filter(Boolean).join(' · ')}
        </span>
      </span>
      {entry && (
        <ToneBadge tone={isOrderComplete(entry) ? 'success' : 'info'} icon={Truck}>
          En la carga · {scannedCountOf(entry)} de {itemsTotalOf(entry)}
        </ToneBadge>
      )}
    </span>
  );
  const attributes = { 'data-testid': 'dispatch-queue-order', 'data-order-id': row.id, 'data-dispatchable': String(!blocked) };

  if (canLoadByTap(row, entry)) {
    return (
      <li {...attributes}>
        <Button
          variant="choice"
          className="space-y-2"
          disabled={disabled}
          onClick={() => onLoad(row.id)}
          data-testid="dispatch-queue-load"
          data-order-id={row.id}
        >
          {header}
          <TaskMeta task={row} />
        </Button>
      </li>
    );
  }
  return (
    <li
      {...attributes}
      className={clsx(
        'space-y-2 rounded-2xl border bg-white p-4 dark:bg-[#1a1a1a]',
        blocked ? 'border-dashed border-border' : 'border-border'
      )}
    >
      {header}
      <TaskMeta task={row} />
      {blocked && (
        <p
          data-testid="dispatch-blocked-order"
          data-order-id={row.id}
          className="flex items-start gap-2 text-sm font-semibold text-ninja-dark dark:text-white"
        >
          <Ban size={16} aria-hidden="true" className="mt-0.5 shrink-0" />
          No se puede despachar: {row.blocked_reason || 'está bloqueada'}
        </p>
      )}
      {fix && (
        <Button
          variant="outline"
          size="lg"
          fullWidth
          icon={Tag}
          disabled={disabled}
          onClick={onFix}
          data-testid="dispatch-blocked-label"
          data-order-id={row.id}
        >
          {fix.label}
        </Button>
      )}
    </li>
  );
};

/** An order up the load: how many of its items are up, whether it is complete, and why it failed if it did. */
const LoadOrder = ({ entry, locked, onRemove }) => {
  const complete = isOrderComplete(entry);
  const scanned = scannedCountOf(entry);
  const total = itemsTotalOf(entry);
  const name = dispatchOrderName(entry.row);

  return (
    <li
      data-testid="dispatch-load-order"
      data-order-id={entry.row.id}
      data-complete={String(complete)}
      data-scanned={scanned}
      data-total={total}
      data-failed={String(Boolean(entry.error))}
      className={clsx(
        'space-y-3 rounded-2xl border-2 bg-white p-4 dark:bg-[#1a1a1a]',
        complete ? 'border-ninja-green' : 'border-border'
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p data-testid="dispatch-order-title" className="break-words text-base font-bold text-ninja-dark dark:text-white">
            {orderCardTitle(entry.row)}
          </p>
          <p className="text-sm text-text-secondary">{[entry.row.company_name, courierLabelOf(entry.row)].filter(Boolean).join(' · ')}</p>
        </div>
        <Button
          variant="secondary"
          size="lg"
          icon={X}
          disabled={locked}
          aria-label={`Sacar ${name} de la carga`}
          onClick={onRemove}
          data-testid="dispatch-load-remove"
        >
          Sacar
        </Button>
      </div>
      <ProgressBar value={scanned} max={total} label={progressText(entry)} />
      {entry.error ? (
        <p className="flex items-start gap-2 text-sm font-semibold text-ninja-dark dark:text-white">
          <AlertTriangle size={16} aria-hidden="true" className="mt-0.5 shrink-0 text-amber-600 dark:text-amber-400" />
          <span>No salió: {asSentence(entry.error.message)} Reintenta, o sácala de la carga.</span>
        </p>
      ) : (
        <p className="flex items-center gap-2 text-sm font-semibold text-ninja-dark dark:text-white">
          {complete ? (
            <>
              <CheckCircle2 size={16} aria-hidden="true" className="shrink-0 text-ninja-green" />
              Completa
            </>
          ) : (
            `Incompleta: falta${total - scanned === 1 ? '' : 'n'} ${itemsText(total - scanned)}`
          )}
        </p>
      )}
    </li>
  );
};

/** How the last dispatch went: how many left, and each order that did not, with why. */
const DispatchResult = ({ result }) => {
  const failed = failedResults(result);
  return (
    <ResultCard title={resultTitle(result)} tone={failed.length > 0 ? 'warning' : 'success'} testId="dispatch-result">
      {failed.length > 0 && (
        <>
          <ul className="space-y-2">
            {failed.map((item) => (
              <li
                key={item.order_id}
                data-testid="dispatch-failed-order"
                data-order-id={item.order_id}
                className="rounded-2xl border border-border p-3"
              >
                <p className="text-base font-bold text-ninja-dark dark:text-white">
                  {item.reference_id || `Orden #${item.order_id}`}
                </p>
                <p className="text-sm text-text-secondary">{item.error || 'No se pudo despachar.'}</p>
              </li>
            ))}
          </ul>
          <p className="text-sm text-text-secondary">
            Siguen en la carga. «Reintentar las que fallaron» las revisa antes de que vuelvan a salir.
          </p>
        </>
      )}
    </ResultCard>
  );
};

/**
 * Despacho: the orders ready to leave and the load going up the truck. Every
 * item of an order is scanned — each label, or the shipment's once and then
 * counted —, or the order is tapped and, once a sheet confirms it, all its
 * items go up unscanned; only complete orders leave, whole, once confirmed in
 * a sheet. `?courier=` filters the queue, as a task of the tray opens it.
 */
export const DispatchStation = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const courier = searchParams.get('courier') || '';
  const warehouseId = useSelector(selectCurrentOperatorWarehouseId);
  const flows = useSelector(selectEnabledFlows);
  const courierSend = useSelector(selectCourierSendReady);
  const courierRelabel = useSelector(selectCourierRelabelReady);
  const { queue, load, scan, pending, retrying, result } = useSelector((state) => state.operatorDispatch);
  const summary = useSelector(selectDispatchSummary);
  const accepting = useSelector(selectDispatchAccepting);
  const repeatLabel = useSelector(selectShipmentRepeatLabel);
  const [confirming, setConfirming] = useState(false);
  const [removing, setRemoving] = useState(null);
  /** The order tapped in the queue, while its sheet asks before it goes up. */
  const [offering, setOffering] = useState(null);
  const loadingOrder = useSelector(selectIsRunning(loadDispatchOrder));

  useEffect(() => {
    dispatch(dispatchStationEntered(warehouseId));
    dispatch(taskEntered({ flow: OPERATOR_FLOW.DISPATCH }));
  }, [dispatch, warehouseId]);

  useEffect(() => {
    dispatch(fetchDispatchQueue({ courier, page: 1 }));
  }, [dispatch, courier]);

  const rows = [
    ...queue.items.filter((row) => row.dispatchable !== false),
    ...queue.items.filter((row) => row.dispatchable === false),
  ];
  const complete = summary.complete.length;
  const scannedItems = summary.entries.reduce((total, entry) => total + scannedCountOf(entry), 0);
  const leftOut = leftOutText(summary.incomplete);
  // A row gone from the queue, or up whole meanwhile, closes its sheet.
  const offeredRow = queue.items.find((row) => row.id === offering) || null;
  const offer = offeredRow && canLoadByTap(offeredRow, load.orders[offering]) ? loadOfferSheet(offeredRow, load.orders[offering]) : null;
  // An order whose label is made in packing comes back here, to the same courier's queue; the load stays.
  const query = searchParams.toString();
  const fromHere = returnState(`${OPERATOR_FLOW_PATHS[OPERATOR_FLOW.DISPATCH]}${query ? `?${query}` : ''}`, 'Volver a Despacho');

  // Closed whatever the answer: the band says how it went.
  const confirmLoad = async () => {
    await dispatch(loadDispatchOrder({ orderId: offering }));
    setOffering(null);
  };

  const askRemove = (entry) => {
    if (scannedCountOf(entry) > 0) setRemoving(entry);
    else dispatch(dispatchLoadOrderRemoved(entry.row.id));
  };

  const confirmRemove = () => {
    dispatch(dispatchLoadOrderRemoved(removing.row.id));
    setRemoving(null);
  };

  // Closed whatever the answer: the result or the band says how it went.
  const confirmDispatch = async () => {
    await dispatch(submitDispatch());
    setConfirming(false);
  };

  // Those still complete go only once confirmed, like any dispatch.
  const retryFailed = async () => {
    const action = await dispatch(retryFailedDispatch());
    if (retryFailedDispatch.fulfilled.match(action) && action.payload.checks.some((check) => check.status === RECHECK.READY)) {
      setConfirming(true);
    }
  };

  return (
    <div className="space-y-5" data-testid="dispatch-station">
      <OperatorScanPanel
        prompt="Escanea cada bulto que sube al camión"
        hint="La etiqueta de cada bulto, o la del envío si la orden tiene una sola."
        accepting={accepting}
        lastCode={scan.lastCode}
        busyLabel={pending ? 'Despachando…' : retrying ? 'Revisando…' : 'Buscando…'}
        onCode={(code, source) => dispatch(scanDispatchCode({ code, source }))}
        repeatLabel={repeatLabel || undefined}
        onRepeat={() => dispatch(repeatShipmentItem())}
      />

      {result && <DispatchResult result={result} />}

      <section className="space-y-3" aria-labelledby="dispatch-load-title" data-testid="dispatch-load">
        <div className="flex items-baseline justify-between gap-3">
          <h2 id="dispatch-load-title" className="text-base font-bold text-ninja-dark dark:text-white">
            Carga
          </h2>
          {summary.entries.length > 0 && (
            <span className="text-sm text-text-secondary">
              {ordersText(summary.entries.length)} · {scannedItemsText(scannedItems)}
            </span>
          )}
        </div>
        {summary.mixedCouriers && (
          <div
            data-testid="dispatch-mixed-couriers"
            className="flex items-start gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-amber-800 dark:border-amber-900 dark:bg-amber-900/20 dark:text-amber-300"
          >
            <AlertTriangle size={22} aria-hidden="true" className="mt-0.5 shrink-0" />
            <div>
              <p className="text-base font-bold">{MIXED_COURIERS_TEXT}</p>
              <p className="text-sm">{couriersText(summary.allCouriers)}: cada bulto va en el camión de su courier.</p>
            </div>
          </div>
        )}
        {summary.entries.length === 0 ? (
          <p data-testid="dispatch-load-empty" className="py-6 text-center text-text-secondary">
            Todavía no hay nada en la carga.
          </p>
        ) : (
          <ul className="space-y-3">
            {summary.entries.map((entry) => (
              <LoadOrder key={entry.row.id} entry={entry} locked={!accepting} onRemove={() => askRemove(entry)} />
            ))}
          </ul>
        )}
      </section>

      {/* What is waiting goes last: the scan and the load are what the operator works with. */}
      <section className="space-y-3" aria-labelledby="dispatch-queue-title">
        <div className="flex items-baseline justify-between gap-3">
          <h2 id="dispatch-queue-title" className="text-base font-bold text-ninja-dark dark:text-white">
            Por despachar
          </h2>
          {!queue.loading && <span className="text-sm text-text-secondary">{ordersText(queue.count)}</span>}
        </div>
        {queue.couriers.length > 0 && (
          <CourierChips
            couriers={queue.couriers}
            active={courier}
            onChange={(code) => setSearchParams(code ? { courier: code } : {}, { replace: true })}
          />
        )}
        <p data-testid="dispatch-queue-hint" className="text-sm text-text-secondary">
          Toca una orden para subirla con todos sus bultos: antes te pide confirmar.
        </p>
        <QueueList
          queue={queue}
          testId="dispatch-queue"
          emptyText={courier ? 'Este courier no tiene órdenes por despachar.' : 'No hay órdenes por despachar.'}
          onRetry={() => dispatch(fetchDispatchQueue({ courier, page: 1 }))}
          onMore={() => dispatch(fetchDispatchQueue({ courier, page: queue.page + 1 }))}
          onRefresh={() => dispatch(fetchDispatchQueue({ courier, page: 1 }))}
          refreshThunk={fetchDispatchQueue}
        >
          {rows.map((row) => {
            const fix = labelFixOf(row, { flows, courierSend, courierRelabel });
            return (
              <QueueOrder
                key={row.id}
                row={row}
                entry={load.orders[row.id]}
                disabled={!accepting}
                onLoad={setOffering}
                fix={fix}
                onFix={() => navigate(fix.to, { state: fromHere })}
              />
            );
          })}
        </QueueList>
      </section>

      <OperatorActionBar>
        {summary.failed.length > 0 ? (
          <PendingButton
            pendingOn={retryFailedDispatch}
            variant="primary"
            size="lg"
            icon={RotateCcw}
            disabled={!accepting}
            loadingLabel="Revisando…"
            onClick={retryFailed}
            data-testid="dispatch-retry-failed"
          >
            Reintentar las que fallaron
          </PendingButton>
        ) : (
          <PendingButton
            pendingOn={dispatchLoad}
            variant="primary"
            size="lg"
            icon={complete > 0 ? Truck : undefined}
            disabled={complete === 0 || !accepting}
            loadingLabel="Despachando…"
            onClick={() => setConfirming(true)}
            data-testid="dispatch-submit"
          >
            {submitLabel(summary)}
          </PendingButton>
        )}
      </OperatorActionBar>

      <ConfirmSheet
        isOpen={confirming && complete > 0}
        title="¿Despachar la carga?"
        message={`Salen ${ordersText(complete)} · ${itemsText(summary.items)}, cada una entera.`}
        items={summary.couriers.map((item) => ({
          key: item.code ?? 'none',
          title: item.label,
          subtitle: `${ordersText(item.orders)} · ${itemsText(item.items)}`,
        }))}
        confirmLabel={submitLabel(summary)}
        busy={pending}
        busyLabel="Despachando…"
        onConfirm={confirmDispatch}
        onClose={() => setConfirming(false)}
        testId="dispatch-confirm-sheet"
        confirmTestId="dispatch-confirm"
      >
        {summary.couriers.length > 1 && (
          <p
            data-testid="dispatch-confirm-mixed"
            className="flex items-start gap-2 rounded-2xl border border-amber-200 bg-amber-50 p-3 text-sm font-semibold text-amber-800 dark:border-amber-900 dark:bg-amber-900/20 dark:text-amber-300"
          >
            <AlertTriangle size={18} aria-hidden="true" className="shrink-0" />
            {MIXED_COURIERS_TEXT}: {couriersText(summary.couriers)}.
          </p>
        )}
        {leftOut && (
          <p data-testid="dispatch-confirm-left-out" className="text-sm font-semibold text-ninja-dark dark:text-white">
            {leftOut} Sus bultos no salen.
          </p>
        )}
      </ConfirmSheet>

      <ConfirmSheet
        isOpen={Boolean(offer)}
        title={offer?.title || ''}
        message={offer?.message}
        items={offer?.items}
        confirmLabel={offer?.confirmLabel || ''}
        busy={loadingOrder}
        busyLabel="Revisando…"
        onConfirm={confirmLoad}
        onClose={() => setOffering(null)}
        testId="dispatch-load-sheet"
        confirmTestId="dispatch-load-confirm"
      />

      <ConfirmSheet
        isOpen={Boolean(removing)}
        title={`¿Sacar ${removing ? dispatchOrderName(removing.row) : ''} de la carga?`}
        message={removing ? takeOffLoadText(removing) : ''}
        confirmLabel="Sacar de la carga"
        confirmVariant="danger"
        onConfirm={confirmRemove}
        onClose={() => setRemoving(null)}
        testId="dispatch-remove-sheet"
      />
    </div>
  );
};
